import { create } from "zustand";
import { type Tenant } from "@/entities/tenant";
import { useAuthStore } from "@/features/auth/model";
import { useUserScopeStore } from "@/features/auth/model/user-scope.store";

interface TenantStore {
  tenants: Tenant[];
  currentTenant: Tenant | null;
  setTenants: (tenants: Tenant[]) => void;
  addTenant: (tenant: Tenant) => void;
  updateTenant: (id: string, tenant: Partial<Tenant>) => void;
  deleteTenant: (id: string) => void;
  setCurrentTenant: (tenant: Tenant | null) => void;
  syncCurrentTenantFromAuth: () => void;
}

/**
 * Get the current tenant from authenticated user's account
 * This syncs with the user scope store (which has account details) or auth store (which has tenant_id)
 */
const getCurrentTenantFromAuth = (): Tenant | null => {
  // First, try to get from user scope store (has full account details)
  const scope = useUserScopeStore.getState().scope;
  if (scope?.account) {
    return {
      id: scope.account.id,
      name: scope.account.name || "",
      createdAt: new Date().toISOString(), // Account details don't include dates, use current date as fallback
      updatedAt: new Date().toISOString(),
    };
  }

  // Fallback: get from auth store (user has tenant_id which is accountId)
  const user = useAuthStore.getState().user;
  if (user?.tenant_id) {
    return {
      id: user.tenant_id,
      name: "", // Name not available from user, will be empty
      createdAt: user.createdAt ?? new Date().toISOString(),
      updatedAt: user.updatedAt ?? new Date().toISOString(),
    };
  }

  return null;
};

export const useTenantStore = create<TenantStore>((set, get) => ({
  tenants: [],
  currentTenant: null,

  setTenants: (tenants) => set({ tenants }),

  addTenant: (tenant) => set((state) => ({ tenants: [...state.tenants, tenant] })),

  updateTenant: (id, updatedTenant) =>
    set((state) => ({
      tenants: state.tenants.map((t) => (t.id === id ? { ...t, ...updatedTenant } : t)),
      currentTenant:
        state.currentTenant?.id === id
          ? { ...state.currentTenant, ...updatedTenant }
          : state.currentTenant,
    })),

  deleteTenant: (id) => set((state) => ({ tenants: state.tenants.filter((t) => t.id !== id) })),

  setCurrentTenant: (tenant) => set({ currentTenant: tenant }),

  /**
   * Sync current tenant from authentication state
   * Should be called after login or when user scope is loaded
   */
  syncCurrentTenantFromAuth: () => {
    const tenant = getCurrentTenantFromAuth();
    if (tenant) {
      set({ currentTenant: tenant });
      // Also add to tenants list if not already present
      const state = get();
      if (!state.tenants.find((t) => t.id === tenant.id)) {
        set({ tenants: [...state.tenants, tenant] });
      }
    } else {
      // Clear tenant if user is logged out
      set({ currentTenant: null });
    }
  },
}));

// Subscribe to user scope changes to auto-sync tenant
useUserScopeStore.subscribe((state, prevState) => {
  if (state.scope !== prevState.scope) {
    useTenantStore.getState().syncCurrentTenantFromAuth();
  }
});

// Subscribe to auth changes to auto-sync tenant
useAuthStore.subscribe((state, prevState) => {
  if (state.user !== prevState.user) {
    useTenantStore.getState().syncCurrentTenantFromAuth();
  }
});
